import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Folio, Engraving, Glossarium, Marginalia, StaffExample } from "@/components/sn/SNShell";
import { Notation } from "@/components/Notation";

export default function StaffNotation6() {
  const { lesson, course, accent } = findLesson("staff-notation", "6");
  return (
    <LessonShell
      course={course}
      courseSlug="staff-notation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/staff-notation/SN6-Dotted-Tied-Animated.mp4"
        caption="Dotted notes (add ½ the value) & tied notes (add durations)"
        hint="≈ 26 sec · watch first"
      />
      <Folio number="I" title="Two ways to make a note longer">
        <p>
          The note-value system from Lesson 4 gives you clean powers of two: 4, 2, 1, ½, ¼.
          But real music demands notes that last 3 beats, or 1.5 beats, or that cross a barline
          and continue into the next bar. For this, notation has two tools: the <strong>dot</strong>{" "}
          and the <strong>tie</strong>.
        </p>
        <p>
          Jason draws the distinction immediately: <em>"Both of them will extend the value of
          a note. But the way they extend it is the question."</em> A dotted note extends by a
          fixed rule. A tied note extends to another note. Same outcome — a longer duration —
          but completely different mechanics.
        </p>
      </Folio>

      <Folio number="II" title="The dot — always adds half">
        <p>
          A dot placed to the right of any note head adds exactly <strong>half of that note's
          own value</strong>. The formula never changes: dotted note = note + (note ÷ 2).
        </p>
        <Glossarium>
          <p>
            <strong>Dotted whole note</strong> — 4 + (half of 4) = 4 + 2 = <strong>6 beats</strong>
          </p>
          <p>
            <strong>Dotted half note</strong> — 2 + (half of 2) = 2 + 1 = <strong>3 beats</strong>.
            This fills an entire 3/4 bar. A waltz bar is one dotted half note.
          </p>
          <p>
            <strong>Dotted quarter note</strong> — 1 + (half of 1) = 1 + 0.5 ={" "}
            <strong>1.5 beats</strong>. Very common in compound-feel rhythms.
          </p>
          <p>
            <strong>Dotted eighth note</strong> — 0.5 + (half of 0.5) = 0.5 + 0.25 ={" "}
            <strong>0.75 beats</strong> — three fourths of a beat, as Jason puts it. "If you know
            your fractions it will be three fourths of a beat."
          </p>
          <p>
            <strong>Dotted sixteenth note</strong> — 0.25 + 0.125 = <strong>0.375 beats</strong>.
            "Not that this is important — it's just good to know," Jason says. Academically
            complete, practically rare.
          </p>
        </Glossarium>
        <p>
          The deeper point: with all these dotted values available, "you can get a note to be
          any value" — 3 beats, 1.5, 0.75, 0.375, and more once ties join in. The
          dot is how notation escapes the binary grid of powers of two.
        </p>
      </Folio>

      <StaffExample caption="Dotted half (3 beats) + quarter (1 beat) = a full 4/4 bar. Dotted quarter (1.5 beats) + eighth + two quarters = another full bar.">
        <Notation
          measures={[
            {
              chords: [
                { keys: ["C4"], duration: "hd" },
                { keys: ["G4"], duration: "q" },
              ],
            },
            {
              chords: [
                { keys: ["C4"], duration: "qd" },
                { keys: ["E4"], duration: "8" },
                { keys: ["G4"], duration: "q" },
                { keys: ["C5"], duration: "q" },
              ],
            },
          ]}
          timeSig="4/4"
          clef="treble"
          width={620}
          height={150}
        />
      </StaffExample>

      <Folio number="III" title="The tie — extend to another note">
        <p>
          A tie is a curved line connecting two notes of the <em>same pitch</em>. The instruction
          it carries: play the first note, then hold it through the second note's duration without
          re-striking. You attack once; you hear one long sound; you see two note heads connected
          by a curve.
        </p>
        <p>
          Jason's exact words: <em>"I'm not going to be hitting this note. I won't be hitting
          this, rather I'll be tying it."</em>{" "} And again: <em>"I'll be hitting this note and
          not hitting this note."</em> The attack happens once only. The duration extends silently through
          the tie.
        </p>
        <p>
          The classic use case: a note that must cross a barline. In a 4/4 bar, you cannot write
          a single note lasting 5 beats — only 4 beats remain in the bar. You write a whole note
          (4 beats), tie it to a quarter note (1 beat) at the start of the next bar. Total: 5
          beats, notated cleanly across two bars.
        </p>

        <StaffExample caption="The 5-beat note. Bar 1 is a whole note; the curve carries it over the barline into the quarter note that opens bar 2 — struck once, held for five beats. Bar 2 then finishes with a dotted half (3 beats), so both bars still add up to four.">
          <Notation
            measures={[
              {
                chords: [{ keys: ["C4"], duration: "w", tieToNext: true }],
              },
              {
                chords: [
                  { keys: ["C4"], duration: "q" },
                  { keys: ["G4"], duration: "hd" },
                ],
              },
            ]}
            timeSig="4/4"
            clef="treble"
            width={620}
            height={150}
          />
        </StaffExample>
        <Glossarium>
          <p>
            <strong>Tie</strong> — curved line connecting two notes of the <em>same pitch</em>.
            Play as one long note. Do not re-attack the second note head.
          </p>
          <p>
            <strong>Slur</strong> — curved line connecting notes of <em>different pitches</em>.
            Play legato (smoothly connected), but all notes are struck. Same visual shape,
            completely different musical meaning.
          </p>
        </Glossarium>
        <Marginalia>
          The tie can also link more than two notes — you can tie a note across several bars if
          needed. Each tied note is held, never re-attacked, until the final note in the chain
          ends or is released.
        </Marginalia>
      </Folio>

      <Folio number="IV" title="When to use a dot and when to use a tie">
        <p>
          A dotted note is self-contained — it tells you the full duration in one symbol. Use
          a dot when the duration falls within a bar and can be expressed as note + half.
        </p>
        <p>
          Use a tie when:
        </p>
        <ul>
          <li>
            The note must <strong>cross a barline</strong>. No single note symbol can span
            two bars — you must tie.
          </li>
          <li>
            The duration <strong>cannot be expressed by any dotted value</strong>. A 5-beat
            note has no dotted symbol; you need a 4-beat whole tied to a 1-beat quarter.
          </li>
          <li>
            The notation needs to show clearly <strong>where the barline falls</strong> —
            even if theoretically a dotted note could work, a tie can make the pulse more
            legible to the performer.
          </li>
        </ul>
        <p>
          Both tools exist because music doesn't always line up with the bar. The bar is a
          container; the melody floats through it. Dots and ties are how the notation catches
          the melody's shape without distorting the grid.
        </p>
      </Folio>

      <Engraving
        src="/notes/staff-notation/finals/06-dotted-and-tied-notes-page1.png"
        caption="Jason's note: dot = add half the note's value. Tie = extend to another note. Both make notes longer; only one crosses the barline."
      />

      <Folio number="V" title="The ecosystem — complete">
        <p>
          At the end of this lesson, Jason runs through everything the six lessons of this
          course have covered — and it maps precisely onto the three-dimensional framework from
          Lesson 1:
        </p>
        <Glossarium>
          <p>
            <strong>Evolution and purpose</strong> (Lesson 1) — why notation exists, the three
            approaches (read and play, read and sing, listen and write), and the three dimensions
            at macro and micro level.
          </p>
          <p>
            <strong>Notes on the treble clef</strong> (Lesson 2) — G clef, thirds as the master
            key, lines E G B D F, spaces F A C E, ledger lines, accidentals, key signatures.
          </p>
          <p>
            <strong>Notes on the bass clef</strong> (Lesson 3) — F clef, lines G B D F A, spaces
            A C E G, middle C as the meeting point of both clefs.
          </p>
          <p>
            <strong>Note duration — micro</strong> (Lesson 4) — hit, miss, or hold; the beat as
            a time container; semibreve through semiquaver; beaming; counting subdivisions.
          </p>
          <p>
            <strong>Time signature — macro</strong> (Lesson 5) — top and bottom numbers; 4/4 = 4
            × 1/4; building bars from note-value arithmetic; rests in the bar.
          </p>
          <p>
            <strong>Dotted and tied notes</strong> (this lesson) — extending duration beyond
            powers of two, crossing barlines, the complete note-duration toolkit.
          </p>
        </Glossarium>
        <p>
          "We've covered the whole ecosystem of staff notation," Jason says at the close.
          Now comes the work: read every score you find. Sing it, play it, write it. All three
          approaches, as often as you can manage. Notation is a language — and like any language,
          fluency comes only through use.
        </p>
      </Folio>
    </LessonShell>
  );
}
